import { h } from '@omni-torn/ui';
import {
  favoritesDoc,
  GEAR_TYPES,
  MARKET_FAVORITES_LIST,
  MARKET_FAVORITES_MAX,
  meetsTarget,
  POOL_KEEP_MS,
  readFavorites,
  searchItems,
  type AuctionSale,
  type DealRow,
  type DealsAnswer,
  type GearAnswer,
  type GearBonus,
  type GearRow,
  type MarketFavorite,
  type PoolListing,
} from '@omni-torn/shared';
import type { RefItem } from '@omni-torn/shared/torn-ref';
import { server } from '../session';
import { ago, box, ext, loading, money, num, problem, table } from '../ui';

/**
 * Market → Gear (website roadmap S31) and Market → Deals (S32): Omni-Torn's market pool (apps/api/src/market-pool.ts).
 *
 * - Listings Omni-Torn users SAW on Torn's Item Market, bazaar and auction pages they were viewing (terms version 15; never
 *   who saw them), kept 24 hours; one item asked on Gear also shows Torn's own Item Market copies (Torn's API names no
 *   sellers) and its finished auctions. RW values: the median of comparable finished auctions (Torn's public auction-house
 *   list, read by the server; no sellers or buyers kept).
 * - Deals: listings seen in the last 2 hours under Torn's market value, bazaars at $1, and your favorites — your own list
 *   with a target price, kept with your lists (`/api/me/lists`, synced with the extension). Alerts come later (S34).
 * - Sellers' names are what Torn shows publicly on those pages; links go to Torn, where the player clicks.
 */

const BAZAAR_URL = (player: number, item: number) => `https://www.torn.com/bazaar.php?userId=${player}&itemId=${item}#/`;
const MARKET_URL = (id: number) => `https://www.torn.com/page.php?sid=ItemMarket#/market/view=search&itemID=${id}`;
const AUCTION_URL = 'https://www.torn.com/amarket.php';

const pct = (p: number | null): string => (p === null ? '—' : `${p > 0 ? '+' : p < 0 ? '−' : '±'}${num(Math.abs(p), 1)}%`);
const bonusText = (b: readonly GearBonus[]): string => b.map((x) => `${x.title}${x.value !== null ? ` ${num(x.value, 1)}%` : ''}`).join(', ') || '—';
const rarityCell = (r: PoolListing['rarity']) => (r ? h('span', { class: `rarity-text-${r}` }, r) : h('span', { class: 'muted' }, '—'));
const statsText = (l: { stats: PoolListing['stats'] }): string => {
  const s = l.stats;
  if (!s) return '—';
  return [s.damage ? `${num(s.damage, 2)} dmg` : '', s.accuracy ? `${num(s.accuracy, 2)} acc` : '', s.armor ? `${num(s.armor, 2)} arm` : '', s.quality ? `${num(s.quality, 0)}% q` : ''].filter(Boolean).join(' · ') || '—';
};

/** Where to buy it: the seller's bazaar, Torn's Item Market or the auction house (plain links the player clicks). */
function whereCell(l: PoolListing): HTMLElement {
  const who = l.sellerName ?? (l.sellerId ? `#${l.sellerId}` : null);
  if (l.source === 'bazaar' && l.sellerId) return h('span', {}, ext(BAZAAR_URL(l.sellerId, l.itemId), `${who}’s bazaar`));
  if (l.source === 'auction') return h('span', {}, ext(AUCTION_URL, 'Auction'), who ? h('span', { class: 'muted small' }, ` · ${who}`) : null, l.endsAt ? h('div', { class: 'muted small' }, `ends ${new Date(l.endsAt).toUTCString().slice(17, 22)} TCT`) : null);
  return h('span', {}, ext(MARKET_URL(l.itemId), 'Item Market'), h('span', { class: 'muted small' }, who ? ` · ${who}` : ' · seller not shown'));
}

export function marketTabs(current: 'prices' | 'gear' | 'deals'): HTMLElement {
  const link = (id: string, label: string, href: string) => h('a', { href, 'data-link': true, class: `btn-small${id === current ? ' active' : ''}`, 'aria-current': id === current ? 'page' : 'false', 'data-market-tab': id }, label);
  return h('nav', { class: 'toolbar market-tabs', 'aria-label': 'Market' }, link('prices', 'Prices', '/market'), link('gear', 'Gear', '/market/gear'), link('deals', 'Deals', '/market/deals'));
}

// ---------------------------------------------------------------- Gear (S31)

export async function gearView(body: HTMLElement, items: readonly RefItem[]): Promise<void> {
  const q = new URLSearchParams(location.search);
  const gearItems = items.filter((i) => (GEAR_TYPES as readonly string[]).includes(i.type));
  const byId = new Map(items.map((i) => [i.id, i]));
  const field = (name: string, el: HTMLElement, label: string) => h('label', {}, `${label} `, el);
  const input = (name: string, attrs: Record<string, string> = {}) => h('input', { name, value: q.get(name) ?? '', 'data-filter': name, ...attrs }) as HTMLInputElement;
  const select = (name: string, opts: Array<[string, string]>) => {
    const s = h('select', { name, 'data-filter': name }, ...opts.map(([v, l]) => h('option', { value: v }, l))) as HTMLSelectElement;
    s.value = q.get(name) ?? opts[0]![0];
    return s;
  };
  const itemIn = input('itemName', { type: 'search', placeholder: 'Any weapon or armor', list: 'gear-items', maxlength: '40', value: byId.get(Number(q.get('item')))?.name ?? '' });
  const form = h(
    'form',
    { class: 'toolbar gear-filter', 'data-market': 'gear' },
    field('itemName', itemIn, 'Item'),
    h('datalist', { id: 'gear-items' }, ...gearItems.map((i) => h('option', { value: i.name }))),
    field('type', select('type', [['', 'Any'], ...GEAR_TYPES.map((t): [string, string] => [t, t])]), 'Type'),
    field('rarity', select('rarity', [['', 'Any'], ['yellow', 'Yellow+'], ['orange', 'Orange+'], ['red', 'Red']]), 'Rarity'),
    field('bonus', input('bonus', { placeholder: 'e.g. Expose', maxlength: '30' }), 'Bonus'),
    field('minBonus', input('minBonus', { type: 'number', min: '0', step: '1', class: 'num-input' }), 'Bonus at least'),
    field('maxPrice', input('maxPrice', { type: 'number', min: '0', step: '1', class: 'num-input' }), 'Price up to $'),
    field('source', select('source', [['', 'Anywhere'], ['itemmarket', 'Item Market'], ['bazaar', 'Bazaars'], ['auction', 'Auctions']]), 'Where'),
    field('sort', select('sort', [['bonus', 'Biggest bonus'], ['vsValue', 'Furthest under RW value'], ['price', 'Cheapest'], ['damage', 'Damage'], ['accuracy', 'Accuracy'], ['armor', 'Armor'], ['quality', 'Quality'], ['rarity', 'Rarity']]), 'Sort'),
    h('button', { type: 'submit' }, 'Show'),
  );
  const out = h('div', {}, loading('Reading the gear for sale…'));
  const salesBox = h('div', {});
  body.replaceChildren(box('Gear for sale', [form, out], { id: 'market-gear' }), salesBox);

  const load = async () => {
    const f = new FormData(form);
    const p = new URLSearchParams();
    const name = String(f.get('itemName') ?? '').trim();
    const item = name ? (searchItems(gearItems, name, 1)[0] ?? null) : null;
    if (item) p.set('item', String(item.id));
    for (const k of ['type', 'rarity', 'bonus', 'minBonus', 'maxPrice', 'source', 'sort']) {
      const v = String(f.get(k) ?? '').trim();
      if (v && !(k === 'sort' && v === 'bonus')) p.set(k, v);
    }
    history.replaceState(null, '', `/market/gear${p.size ? `?${p}` : ''}`);
    out.replaceChildren(loading('Reading the gear for sale…'));
    let a: GearAnswer;
    try {
      a = await server<GearAnswer>('GET', `/api/market/gear${p.size ? `?${p}` : ''}`);
    } catch (err) {
      out.replaceChildren(problem(err));
      return;
    }
    if (!body.isConnected) return;
    out.replaceChildren(
      table(
        a.rows,
        [
          { label: 'Item', get: (r: GearRow) => h('span', {}, h('a', { href: `/market/${r.itemId}`, 'data-link': true }, r.itemName ?? `#${r.itemId}`), r.itemType ? h('div', { class: 'muted small' }, r.itemType) : null), sort: (r) => r.itemName ?? '' },
          { label: 'Rarity', get: (r) => rarityCell(r.rarity), sort: (r) => ['yellow', 'orange', 'red'].indexOf(r.rarity ?? '') },
          { label: 'Bonuses', get: (r) => h('span', { 'data-bonus': '' }, bonusText(r.bonuses)), sort: (r) => r.bonuses[0]?.value ?? 0 },
          { label: 'Stats', get: (r) => h('span', { class: 'small' }, statsText(r)) },
          { label: 'Price', get: (r) => money(r.price), sort: (r) => r.price, cls: 'r' },
          { label: 'RW value', get: (r) => (r.rw ? h('span', { title: `Median of ${r.rw.comps} finished auctions (${r.rw.basis}); middle half ${money(r.rw.low)}–${money(r.rw.high)}` }, money(r.rw.value)) : h('span', { class: 'muted' }, '—')), sort: (r) => r.rw?.value ?? 0, cls: 'r' },
          { label: 'vs value', get: (r) => h('span', { class: r.vsValue === null ? 'muted' : r.vsValue < 0 ? 't-green' : 't-red', 'data-vs': r.vsValue === null ? '' : String(r.vsValue) }, pct(r.vsValue)), sort: (r) => r.vsValue ?? 1e9, cls: 'r' },
          { label: 'Where', get: (r) => whereCell(r) },
          { label: 'Seen', get: (r) => h('span', { class: 'muted small' }, ago(r.seenAt)), sort: (r) => r.seenAt, cls: 'r' },
        ],
        { empty: 'No gear for sale matches — Omni-Torn users haven’t seen any lately, or try fewer filters.' },
      ),
      h('p', { class: 'muted small', 'data-note': 'gear' }, `${num(a.matched)} match${a.matched === 1 ? 'es' : ''}${a.matched > a.rows.length ? ` (the first ${num(a.rows.length)} shown)` : ''}. Listings Omni-Torn users saw on Torn’s Item Market, bazaar and auction pages (from terms version ${a.termsNeeded}, kept ${POOL_KEEP_MS / 3_600_000} hours, never who saw them)${item ? ', and Torn’s own Item Market copies of this item' : ''}. RW values: the middle price of comparable finished auctions — ${num(a.salesKept)} kept${a.salesNewest ? `, newest ${ago(a.salesNewest)}` : ''}. A listing can be gone before you get there.`),
    );
    salesBox.replaceChildren(
      item && a.sales.length
        ? box(
            `Finished auctions · ${item.name}`,
            table(
              a.sales,
              [
                { label: 'Ended', get: (s: AuctionSale) => h('span', { class: 'muted small' }, ago(s.endedAt)), sort: (s) => s.endedAt },
                { label: 'Rarity', get: (s) => rarityCell(s.rarity), sort: (s) => ['yellow', 'orange', 'red'].indexOf(s.rarity ?? '') },
                { label: 'Bonuses', get: (s) => bonusText(s.bonuses) },
                { label: 'Stats', get: (s) => h('span', { class: 'small' }, statsText(s)) },
                { label: 'Bids', get: (s) => num(s.bids), sort: (s) => s.bids, cls: 'r' },
                { label: 'Sold for', get: (s) => money(s.price), sort: (s) => s.price, cls: 'r' },
              ],
              { empty: '' },
            ),
            { id: 'market-auction-sales' },
          )
        : '',
    );
  };
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void load();
  });
  await load();
}

// ---------------------------------------------------------------- Deals (S32)

export async function dealsView(body: HTMLElement, items: readonly RefItem[]): Promise<void> {
  const byId = new Map(items.map((i) => [i.id, i]));
  let favs: MarketFavorite[] = [];
  let favProblem = '';
  const minIn = h('input', { type: 'number', min: '0', max: '95', step: '1', value: new URLSearchParams(location.search).get('minPct') ?? '5', class: 'num-input', 'data-filter': 'minPct', 'aria-label': 'At least this percent under market value' }) as HTMLInputElement;
  const dealsOut = h('div', {}, loading('Reading the deals…'));
  const dollarOut = h('div', {});
  const favOut = h('div', {});
  body.replaceChildren(
    box('Under market value', [h('form', { class: 'toolbar', 'data-market': 'deals', onsubmit: (e: Event) => (e.preventDefault(), void load()) }, h('label', {}, 'At least ', minIn, ' % under Torn’s market value'), h('button', { type: 'submit' }, 'Show')), dealsOut], { id: 'market-deals' }),
    box('$1 bazaars', dollarOut, { id: 'market-dollar' }),
    box('Your favorites', favOut, { id: 'market-favorites' }),
  );

  const cols = (withWhy: boolean) => [
    { label: 'Item', get: (r: DealRow) => h('a', { href: `/market/${r.itemId}`, 'data-link': true }, r.itemName ?? byId.get(r.itemId)?.name ?? `#${r.itemId}`), sort: (r: DealRow) => r.itemName ?? '' },
    { label: 'Where', get: (r: DealRow) => whereCell(r) },
    { label: 'Price', get: (r: DealRow) => money(r.price), sort: (r: DealRow) => r.price, cls: 'r' },
    { label: 'Amount', get: (r: DealRow) => num(r.qty), sort: (r: DealRow) => r.qty, cls: 'r' },
    { label: 'Market value', get: (r: DealRow) => money(r.value), sort: (r: DealRow) => r.value ?? 0, cls: 'r' },
    ...(withWhy ? [{ label: 'Under value', get: (r: DealRow) => h('span', { class: 't-green' }, r.below === null ? '—' : `${num(r.below, 1)}%`), sort: (r: DealRow) => r.below ?? 0, cls: 'r' }] : []),
    { label: 'Profit (resold at value)', get: (r: DealRow) => money(r.profit), sort: (r: DealRow) => r.profit ?? 0, cls: 'r' },
    { label: 'Seen', get: (r: DealRow) => h('span', { class: 'muted small' }, ago(r.seenAt)), sort: (r: DealRow) => r.seenAt, cls: 'r' },
  ];

  const readFavs = async (): Promise<{ lists: Record<string, unknown> | null; updatedAt: number | null }> => {
    const doc = await server<{ lists: Record<string, unknown> | null; updatedAt: number | null }>('GET', '/api/me/lists');
    favs = readFavorites(doc.lists);
    return doc;
  };
  /** Change the favorites in your lists (the server's copy fresh; sent again once if another device wrote between). */
  const saveFavs = async (change: (f: MarketFavorite[]) => MarketFavorite[]): Promise<void> => {
    for (let i = 0; i < 2; i++) {
      const doc = await readFavs();
      const next = change(favs);
      const r = await server<{ applied: boolean; lists: Record<string, unknown> }>('PUT', '/api/me/lists', { lists: { ...(doc.lists ?? {}), [MARKET_FAVORITES_LIST]: favoritesDoc(next) }, updatedAt: Date.now(), device: 'Website', base: doc.updatedAt ?? null });
      if (r.applied) {
        favs = readFavorites(r.lists);
        return;
      }
    }
    throw new Error('Another device changed your lists just now — try again.');
  };

  let answer: DealsAnswer | null = null;
  const drawFavs = () => {
    const nameIn = h('input', { type: 'search', list: 'deal-items', placeholder: 'Item name', maxlength: '40', 'data-fav': 'item', 'aria-label': 'Item' }) as HTMLInputElement;
    const targetIn = h('input', { type: 'number', min: '0', step: '1', placeholder: 'Target $', class: 'num-input', 'data-fav': 'target', 'aria-label': 'Target price' }) as HTMLInputElement;
    const msg = h('span', { class: 'muted small' });
    const add = h(
      'form',
      {
        class: 'toolbar',
        'data-market': 'fav-add',
        onsubmit: (e: Event) => {
          e.preventDefault();
          const it = searchItems(items, nameIn.value, 1)[0];
          if (!it) return void (msg.textContent = 'No item by that name.');
          const target = Math.max(0, Math.round(Number(targetIn.value) || 0));
          msg.textContent = 'Saving…';
          saveFavs((f) => [...f.filter((x) => x.itemId !== it.id), { itemId: it.id, target, at: Date.now() }].slice(-MARKET_FAVORITES_MAX))
            .then(() => load())
            .catch((err) => (msg.textContent = err instanceof Error ? err.message : 'Couldn’t save.'));
        },
      },
      nameIn,
      h('datalist', { id: 'deal-items' }, ...items.filter((i) => i.tradable !== false).map((i) => h('option', { value: i.name }))),
      targetIn,
      h('button', { type: 'submit' }, 'Add / change'),
      msg,
    );
    const cheapest = new Map((answer?.favorites ?? []).map((r) => [r.itemId, r]));
    favOut.replaceChildren(
      add,
      favProblem
        ? h('p', { class: 'muted' }, favProblem)
        : table(
            favs,
            [
              { label: 'Item', get: (f: MarketFavorite) => h('a', { href: `/market/${f.itemId}`, 'data-link': true }, byId.get(f.itemId)?.name ?? `#${f.itemId}`), sort: (f) => byId.get(f.itemId)?.name ?? '' },
              { label: 'Target', get: (f) => (f.target ? money(f.target) : h('span', { class: 'muted' }, 'just watching')), sort: (f) => f.target, cls: 'r' },
              { label: 'Market value', get: (f) => money(byId.get(f.itemId)?.marketValue ?? null), cls: 'r' },
              {
                label: 'Cheapest seen (2 h)',
                get: (f) => {
                  const c = cheapest.get(f.itemId);
                  if (!c) return h('span', { class: 'muted' }, '—');
                  return h('span', { 'data-fav-hit': meetsTarget(f, c.price) ? '1' : '0' }, money(c.price), meetsTarget(f, c.price) ? h('strong', { class: 't-green' }, ' ✓ at or under your target') : '', h('div', { class: 'small' }, whereCell(c)));
                },
                sort: (f) => cheapest.get(f.itemId)?.price ?? 1e15,
                cls: 'r',
              },
              { label: '', get: (f) => h('button', { type: 'button', class: 'btn-small', 'data-fav-remove': String(f.itemId), onclick: () => void saveFavs((l) => l.filter((x) => x.itemId !== f.itemId)).then(() => load()) }, 'Remove') },
            ],
            { empty: 'No favorites yet: add an item and the price you’d buy at.' },
          ),
      h('p', { class: 'muted small' }, 'Your favorites are kept with your lists (the extension syncs them too). Alerts when one is listed at your price come later.'),
    );
  };

  const load = async () => {
    try {
      await readFavs();
      favProblem = '';
    } catch (err) {
      favProblem = err instanceof Error ? err.message : 'Couldn’t read your favorites.';
    }
    const p = new URLSearchParams({ minPct: String(Math.max(0, Math.min(95, Number(minIn.value) || 0))) });
    if (favs.length) p.set('fav', favs.map((f) => f.itemId).join(','));
    try {
      answer = await server<DealsAnswer>('GET', `/api/market/deals?${p}`);
    } catch (err) {
      dealsOut.replaceChildren(problem(err));
      dollarOut.replaceChildren();
      drawFavs();
      return;
    }
    if (!body.isConnected) return;
    const note = `Listings Omni-Torn users saw on Torn’s bazaar and Item Market pages in the last ${answer.freshMs / 3_600_000} hours (from terms version ${answer.termsNeeded}; never who saw them). Prices change fast — a listing can be gone before you get there.`;
    dealsOut.replaceChildren(table(answer.deals, cols(true), { empty: 'No listing that far under market value was seen lately.' }), h('p', { class: 'muted small', 'data-note': 'deals' }, note));
    dollarOut.replaceChildren(table(answer.dollar, cols(false), { empty: 'No $1 bazaar was seen lately.' }), h('p', { class: 'muted small' }, 'Bazaars selling at $1 — often kept for someone, so don’t be surprised if it’s held back.'));
    drawFavs();
  };
  await load();
}
